export type ScheduleEvent = {
  /**
   * Deliberately coarse ("Morning", "Evening") until the exact times are
   * cleared for publication. Shown on both the homepage timeline and the
   * attire page.
   */
  time: string;
  title: string;
  attire?: string;
  /** Anchor id on the attire page. Present iff `attire` is set. */
  attireId?: string;
  /**
   * Line-art marker for the homepage timeline, as a path under `public/`.
   * Homepage only — the attire page doesn't use these.
   */
  icon?: string;
};

export type ScheduleDay = {
  label: string;
  events: ScheduleEvent[];
};

export const SCHEDULE: ScheduleDay[] = [
  {
    label: 'Friday ❃ April 30th, 2027',
    events: [
      {
        time: 'Evening',
        title: 'Welcome Party & Mehendi',
        icon: '/icons/champagne-bottle.svg',
        attire: 'Indian or Western semi-formal',
        attireId: 'welcome-party',
      },
    ],
  },
  {
    label: 'Saturday ❃ May 1st, 2027',
    events: [
      // The flower-decked car — a procession mark for the baraat.
      { time: 'Morning', title: 'Baraat', icon: '/icons/car.svg' },
      {
        time: 'Morning',
        title: 'Hindu Ceremony',
        // Placeholder: a garland reads as the varmala, but swap freely.
        icon: '/icons/flower-wreath.svg',
        attire: 'Indian or Western formal',
        attireId: 'hindu-ceremony',
      },
      { time: 'Afternoon', title: 'Lunch', icon: '/icons/garlic.svg' },
      { time: 'Afternoon', title: 'Break', icon: '/icons/bells.svg' },
      {
        time: 'Evening',
        title: 'Christian Ceremony',
        icon: '/icons/rings.svg',
        attire: 'Black tie or Indian formal',
        attireId: 'western-ceremony',
      },
      {
        time: 'To follow',
        title: 'Cocktail Hour & Reception',
        icon: '/icons/martini.svg',
      },
    ],
  },
];

/** An "example outfits" block: two unlabeled columns plus flavor text. */
export type AttireExamples = {
  /** Two columns of example garment types. Deliberately not gender-labeled. */
  columns: [string[], string[]];
  /** Reassuring flavor text (placeholder for now). */
  note: string;
};

export type AttireSection = {
  id: string;
  title: string;
  /** Display time, e.g. "Evening". Omitted for note-only rows (the break). */
  time?: string;
  /** Display date, e.g. "Saturday, May 1st, 2027". Omitted where not relevant. */
  date?: string;
  /** One or more dress-code lines; the first is the primary code. Omit for a
   *  row that carries only a note (e.g. the afternoon break). */
  dressCodes?: string[];
  /** Optional guidance paragraph shown under the dress code. */
  guidance?: string;
  /** Optional example-outfits block shown below the row. */
  examples?: AttireExamples;
};

export type ShopLink = { label: string; href: string };
export type ShopGroup = {
  heading: string;
  /** Rental options for this garment type (optional). */
  rent?: ShopLink[];
  /** Retailers to buy from. */
  buy: ShopLink[];
};

/** Attire options grouped by garment type, each split into rent vs buy. */
export const SHOP_GROUPS: ShopGroup[] = [
  {
    heading: 'For lehengas, saris, anarkalis, salwars',
    rent: [{ label: 'Boomie', href: 'https://boomie.rent' }],
    buy: [
      { label: 'Lashkaraa', href: 'https://www.lashkaraa.com' },
      { label: 'Aza', href: 'https://www.azafashions.com' },
      { label: 'Utsav', href: 'https://www.utsavfashion.com' },
      { label: 'Andaz', href: 'https://www.andaazfashion.com' },
    ],
  },
  {
    heading: 'For kurtas, bandhgalas, sherwanis',
    rent: [{ label: 'Boomie', href: 'https://boomie.rent' }],
    buy: [
      { label: 'Manyavar', href: 'https://www.manyavar.com' },
      { label: 'Aza', href: 'https://www.azafashions.com' },
    ],
  },
];

/** The rows shown on the Schedule page, in order. Defined explicitly (rather
 *  than derived from SCHEDULE) because the page groups events differently from
 *  the homepage timeline and includes a note-only afternoon break. The ids match
 *  the homepage timeline's attire links (#welcome-party, etc.). */
export const ATTIRE_SECTIONS: AttireSection[] = [
  {
    id: 'welcome-party',
    title: 'Welcome Party & Mehendi',
    date: 'Friday, April 30th, 2027',
    time: 'Evening',
    dressCodes: ['Indian or Western cocktail'],
    guidance:
      'Colorful / festive Indian cocktail attire encouraged! Western cocktail attire is also welcome.',
  },
  {
    id: 'hindu-ceremony',
    title: 'Baraat & Hindu Ceremony',
    date: 'Saturday, May 1st, 2027',
    time: 'Morning',
    dressCodes: ['Indian or Western formal'],
  },
  {
    id: 'afternoon-break',
    title: 'Lunch and Break',
    date: 'Saturday, May 1st, 2027',
    time: 'Afternoon',
    guidance:
      'Guests may return to their hotels to rest and prepare for the evening events. Shuttle schedules and logistics will be shared closer to the wedding.',
  },
  {
    id: 'western-ceremony',
    title: 'Christian Ceremony & Reception',
    date: 'Saturday, May 1st, 2027',
    time: 'Evening',
    dressCodes: ['Black tie optional / Indian or Western formal'],
    guidance:
      'We encourage you to break out your tuxes or gowns if you have them! Dark suits, formal dresses, & Indian formal wear are also welcome.',
  },
];
